import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/components/wpl/image/src";
import { Link } from "@/components/wpl/link/src";
import { Quote } from "@/components/wpl/tab-group/src";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps, Row } from "@/libraries/wpl/foundations/src";

import { getWorkflowSectionImage } from "../../../localized-images";

const sectionStyles = css({
    "alignItems": "center",
    "color": "#ffffff",
    "display": "flex",
    "flexDirection": "column",

    "backgroundImage": `linear-gradient(to right, transparent 50%, #091e42 50%),
        linear-gradient(to bottom, #091e42 0%, #172b4d 100%)`,
    "backgroundRepeat": "no-repeat",

    [media.below.sm]: {
        ".workflow-body": {
            display: "flex",
            flexDirection: "row"
        },

        ".icon-gradient": {
            flex: "1"
        },

        ".workflow-container": {
            flex: "8"
        },

        "backgroundImage":
            "linear-gradient(to bottom, #091e42 0%, #172b4d 100%)",
        "backgroundPosition": "center",
        "backgroundRepeat": "no-repeat",
        "backgroundSize": "cover"
    },

    ".template-items-container": {
        width: "80%"
    },

    ".quote-image": {
        width: "45px"
    },

    ".quote-blurb": {
        fontSize: "1.25rem",
        lineHeight: "1.2"
    },

    ".quote-name": {
        fontSize: "0.875rem",
        lineHeight: "1.25",
        width: "75%"
    },

    ".quote-company": {
        fontSize: "0.75rem !important",
        lineHeight: "2",
        width: "75%"
    },

    ".workflow-paragraph": {
        textAlign: "center"
    },

    ".workflow-container": {
        [media.above.sm]: {
            display: "flex"
        }
    },
    ".workflow-center-left": {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        paddingInlineStart: "32px",

        [media.above.sm]: {
            paddingBlockEnd: `${token("space.400")}`,
            paddingInlineEnd: "10%",
            paddingInlineStart: 0
        }
    },

    ".workflow-center-right": {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        paddingBlockEnd: `${token("space.400")}`,
        paddingInlineStart: "32px",

        [media.above.sm]: {
            paddingInlineStart: "10%"
        }
    }
});

const h3Styles = css({
    textAlign: "center",
    margin: `${token("space.400")} 0 0 0`,
    fontWeight: "normal",
    [media.above.sm]: {
        fontSize: "1.5rem"
    },
    [media.below.sm]: {
        fontSize: "1.3rem"
    }
});

const WorkflowSection = () => {
    const intl = useIntl();
    const localizedImageSrc = getWorkflowSectionImage(intl.locale);

    return (
        <section css={sectionStyles}>
            <h2
                css={css({
                    textAlign: "center",
                    fontWeight: "normal",
                    fontSize: "2.25rem",
                    [media.only.sm]: {
                        fontSize: "2rem"
                    },
                    [media.below.sm]: {
                        fontSize: "1.5rem",
                        margin: "0"
                    }
                })}
            >
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.workflowSection.header"
                    defaultMessage="Easier than ever"
                    values={DEFAULT_VALUES}
                />
            </h2>
            <Row>
                <div className="workflow-body">
                    <div className="icon-gradient">
                        <IconGradient />
                    </div>
                    <div className="workflow-container">
                        <div className="workflow-center-left">
                            <h3 css={h3Styles}>
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.workflowSection.templatesHeader"
                                    defaultMessage="Templates give you a head start"
                                    values={DEFAULT_VALUES}
                                />
                            </h3>
                            <p className="workflow-paragraph">
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.workflowSection.templatesSubheader"
                                    defaultMessage="Get started with ready-made templates."
                                    values={DEFAULT_VALUES}
                                />
                            </p>
                            <div className="template-items-container">
                                <TemplateItem
                                    headerText={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.scrum",
                                        defaultMessage: "Scrum"
                                    })}
                                    image="https://wac-cdn.atlassian.com/dam/jcr:9567a40e-6541-4905-bf08-dfe201aa80e7/illustrations-spot-Agile.svg?cdnVersion=1194"
                                    imageAltText={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.scrumImageAltText",
                                        defaultMessage: "Agile illustration"
                                    })}
                                    href={useLocalizedHref(
                                        "/software/jira/templates/scrum"
                                    )}
                                />
                                <TemplateItem
                                    headerText={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.kanban",
                                        defaultMessage: "Kanban"
                                    })}
                                    image="https://wac-cdn.atlassian.com/dam/jcr:7d0e6516-361d-4ec3-bd0f-22d7c7d105b4/illustrations-spot-JIRA%20Board.svg?cdnVersion=1194"
                                    imageAltText={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.kanbanImageAltText",
                                        defaultMessage: "Kanban illustration"
                                    })}
                                    href={useLocalizedHref(
                                        "/software/jira/templates/kanban"
                                    )}
                                />
                                <TemplateItem
                                    headerText={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.bugTracking",
                                        defaultMessage: "Bug tracking"
                                    })}
                                    image="https://wac-cdn.atlassian.com/dam/jcr:a2b254dd-146a-4ea9-9ae0-ec5d250a9cbf/JSW_ProductTour_DarkModeIllustration_BugTracking_Spot.svg?cdnVersion=1194"
                                    imageAltText={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.bugTrackingImageAltText",
                                        defaultMessage: "Bug illustration"
                                    })}
                                    href={useLocalizedHref(
                                        "/software/jira/templates/bug-tracking"
                                    )}
                                />
                                <TemplateItem
                                    headerText={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.devops",
                                        defaultMessage: "DevOps"
                                    })}
                                    image="https://wac-cdn.atlassian.com/dam/jcr:6505f1fe-f628-460f-987b-c07fab309341/devops-spot-logo.svg?cdnVersion=1194"
                                    imageAltText={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.devopsImageAltText",
                                        defaultMessage: "Devops illustration"
                                    })}
                                    href={useLocalizedHref(
                                        "/software/jira/templates/devops"
                                    )}
                                />
                            </div>
                            <div
                                css={css({
                                    borderRadius: "0px 150px 150px 0px",
                                    marginInlineStart: "5%"
                                })}
                            >
                                <Quote
                                    image={
                                        <Image
                                            className="quote-image"
                                            src="https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220"
                                            alt={intl.formatMessage({
                                                id: "JiraSoftwarePageTemplate.workflowSection.quoteImage",
                                                defaultMessage: "Quotation mark"
                                            })}
                                        />
                                    }
                                    quote={
                                        intl.formatMessage({
                                            id: "JiraSoftwarePageTemplate.workflowSection.quote",
                                            defaultMessage:
                                                "You can decide how you are working as a team first and then let the software work around you."
                                        }) + '"'
                                    }
                                    name={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.name",
                                        defaultMessage: "Maria Bonorchis"
                                    })}
                                    company={intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.workflowSection.company",
                                        defaultMessage:
                                            "Agile Delivery Manager, Cancer Research UK"
                                    })}
                                    colors={{
                                        defaultBG: "#42526e",
                                        defaultText: "#dfe1e6",
                                        activeBG: "#57d9a3",
                                        activeText: "#091e42",
                                        hoverBG: "#172b4d",
                                        hoverText: "#dfe1e6",
                                        quoteColor: "#0065ff"
                                    }}
                                />
                            </div>
                        </div>
                        <div className="workflow-center-right">
                            <h3 css={h3Styles}>
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.workflowSection.customizeHeader"
                                    defaultMessage="Then customize as you grow"
                                    values={DEFAULT_VALUES}
                                />
                            </h3>
                            <p className="workflow-paragraph">
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.workflowSection.customizeSubheader"
                                    defaultMessage="Jira adapts to the way you work, not the other way around. Start simple, customize as you go."
                                    values={DEFAULT_VALUES}
                                />
                            </p>
                            <Image
                                src={localizedImageSrc}
                                alt="Diagram showcasing an example workflow using Jira Software"
                            />
                        </div>
                    </div>
                </div>
            </Row>
        </section>
    );
};

const iconGradientStyles = css({
    "display": "flex",
    "justifyContent": "space-between",
    "maxWidth": "768px",

    "backgroundPosition": "center center",
    "backgroundRepeat": "no-repeat",

    [media.below.sm]: {
        flexDirection: "column",
        backgroundImage: "linear-gradient(to bottom, #0065ff, #79f2c0)",
        backgroundSize: "5px 90%",
        paddingBlockStart: "25%",
        paddingBlockEnd: "25%",
        marginBlockStart: "50%",
        height: "57%"
    },
    [media.above.sm]: {
        flexDirection: "row",
        backgroundImage: "linear-gradient(to right, #0065ff, #79f2c0)",
        backgroundSize: "80% 5px",
        paddingInlineStart: "2%",
        paddingInlineEnd: "2%",
        marginInlineStart: "15%",
        marginInlineEnd: "15%",
        width: "70%"
    },
    ".workflow-icon": {
        width: "56px"
    }
});

const IconGradient = () => {
    const intl = useIntl();

    return (
        <div css={iconGradientStyles}>
            <Image
                className="workflow-icon"
                src="https://wac-cdn.atlassian.com/dam/jcr:ef95fe7f-398f-4690-83af-831ef07adf34/icon-templates.svg?cdnVersion=1216"
                alt={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.workflowSection.templatesIcon",
                    defaultMessage: "Templates icon"
                })}
            />

            <Image
                className="workflow-icon"
                src="https://wac-cdn.atlassian.com/dam/jcr:99363e04-3456-415d-b2e1-b311cc0a8444/icon-workflow.svg?cdnVersion=1216"
                alt={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.workflowSection.workflowIcon",
                    defaultMessage: "Workflow icon"
                })}
            />
        </div>
    );
};

const templateStyles = css({
    "display": "flex",
    "justify": "start",
    "noWrap": "true",
    "margin": `${token("space.400")} 0`,
    "width": "100%",

    ".template-header": {
        paddingBlockStart: `${token("space.100")}`,
        paddingBlockEnd: `${token("space.100")}`,
        fontWeight: "500",
        fontSize: "1.25rem",
        fontFamily: "var(--bxp-charlie-display-font-family)"
    },

    ".template-image-container": {
        alignItems: "center",
        backgroundColor: "#253858",
        display: "flex",
        minHeight: `${token("space.800")}`,
        justifyContent: "center",
        padding: `${token("space.200")}`,
        width: "110px",
        noWrap: "true"
    },

    ".template-link-container": {
        "alignContent": "stretch",
        "backgroundColor": "#172b4d",
        "display": "flex",
        "flexDirection": "column",
        "justifyContent": "center",
        "paddingInlineStart": `${token("space.200")}`,
        "paddingBlockStart": `${token("space.100")}`,
        "paddingBlockEnd": `${token("space.200")}`,
        "width": "100%",

        "h4": {
            margin: "0 0 14px"
        },

        "a,a:focus,a:visited,a:hover": {
            color: "#fafbfc",

            svg: {
                transition: "transform 0.25s ease-in-out",

                path: {
                    fill: "#fafbfc"
                }
            }
        },

        "a:hover": {
            textDecoration: "underline",

            svg: {
                transform: `translateX(${token("space.100")})`
            }
        }
    }
});

const TemplateItem = ({
    headerText,
    image,
    imageAltText,
    href
}: {
    headerText: string;
    image: string;
    imageAltText: string;
    href: string;
}) => {
    const intl = useIntl();

    const dataEventProps: DataEventProps = {
        "data-event-component": "linkButton",
        "data-event-container": "workflowSection",
        "data-event": "clicked",
        "data-product-key": "jiraSoftware"
    };

    return (
        <div css={templateStyles}>
            <div className="template-image-container">
                <Image src={image} alt={imageAltText} />
            </div>
            <div className="template-link-container">
                <div className="template-header">{headerText}</div>
                <Link
                    text={intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.workflowSection.learnMore",
                        defaultMessage: "Learn more"
                    })}
                    href={href}
                    bgColor="bgDark"
                    dataEventProps={dataEventProps}
                />
            </div>
        </div>
    );
};

WorkflowSection.displayName = "WorkflowSection";

export default WorkflowSection;
